import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Network, ShieldCheck, MonitorPlay, Zap, Cpu, Wrench,
  BadgeCheck, ClipboardCheck, Timer, Share2, ShieldAlert,
  Building2, Store, Warehouse, Factory, GraduationCap, HeartPulse, UtensilsCrossed, Home, Snowflake,
  ArrowRight, Mail, ChevronRight,
} from "lucide-react";
import { IMAGES, COMPANY } from "../mock";

export const ICONS = {
  Network, ShieldCheck, MonitorPlay, Zap, Cpu, Wrench,
  BadgeCheck, ClipboardCheck, Timer, Share2, ShieldAlert,
  Building2, Store, Warehouse, Factory, GraduationCap, HeartPulse, UtensilsCrossed, Home, Snowflake,
};

export function Icon({ name, className }) {
  const C = ICONS[name] || Network;
  return <C className={className} />;
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

export function Reveal({ children, className = "", delay = 0, y = 32, as: Tag = "div", once = true }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(e.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref}
      className={`sr ${visible ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms`, "--sr-y": `${y}px` }}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`text-[12px] font-semibold tracking-[0.25em] text-[#1e73ff] uppercase ${className}`}>
      {children}
    </p>
  );
}

export function PageHero({ eyebrow, title, subtitle, crumb, variant = "light" }) {
  const brand = variant === "brand";
  const section = brand
    ? "relative overflow-hidden bg-gradient-to-br from-[#0b2545] to-[#12355f] pt-10 pb-10 lg:pt-12 lg:pb-12"
    : "bg-[#dfeafe] pt-14 pb-20 lg:pt-16 lg:pb-28";
  return (
    <section className={section}>
      {brand && (
        <div
          className="absolute right-0 top-0 h-full w-1/2 opacity-20 bg-cover bg-center hidden md:block pointer-events-none"
          style={{ backgroundImage: `url(${IMAGES.cctvSky})`, mixBlendMode: "luminosity" }}
        />
      )}
      <div className="relative max-w-[1240px] mx-auto px-5 lg:px-8">
        {crumb && (
          <div className={`flex items-center gap-2 text-[13px] mb-8 ${brand ? "text-white/70" : "text-slate-500"}`}>
            <Link to="/" className={brand ? "hover:text-white" : "hover:text-[#0b2545]"}>Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className={brand ? "text-white" : "text-slate-700"}>{crumb}</span>
          </div>
        )}
        <p className={`text-[12px] font-semibold tracking-[0.25em] uppercase mb-4 ${brand ? "text-[#2dd4bf]" : "text-[#1e73ff]"}`}>
          {eyebrow}
        </p>
        <h1 className={`font-display font-extrabold hero-heading max-w-4xl ${brand ? "text-white text-[40px] sm:text-[54px] lg:text-[62px]" : "text-[#0b2545] text-[44px] sm:text-[64px] lg:text-[76px]"}`}>
          {title}
        </h1>
        <div className={`mt-5 h-1.5 w-20 rounded-full ${brand ? "bg-[#2dd4bf]" : "bg-[#1e73ff]"}`} />
        {subtitle && (
          <p className={`mt-6 text-[18px] lg:text-[19px] max-w-2xl leading-relaxed ${brand ? "text-white/90" : "text-slate-600"}`}>
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}

export function CtaBanner() {
  return (
    <section className="py-20 lg:py-24 bg-[#eaf2ff]">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0b2545] to-[#12355f] px-8 py-14 lg:px-16 lg:py-16">
          <div
            className="absolute right-0 top-0 h-full w-1/2 opacity-20 bg-cover bg-center hidden md:block"
            style={{ backgroundImage: `url(${IMAGES.cctvSky})`, mixBlendMode: "luminosity" }}
          />
          <div className="relative max-w-xl">
            <h2 className="font-display font-extrabold text-white text-[38px] lg:text-[52px] hero-heading">
              Ready to Get Started?
            </h2>
            <p className="mt-5 text-[16px] text-slate-300 leading-relaxed">
              From new installations and system upgrades to ongoing technical support, we provide expert solutions tailored to your needs. Contact our team today to discuss your project and request a personalised quotation.
            </p>
            <a href={`mailto:${COMPANY.email}`} className="mt-7 inline-flex items-center gap-2 text-[15px] text-slate-300 hover:text-[#14b8a6] transition-colors">
              <Mail className="w-4 h-4" /> {COMPANY.email}
            </a>
          </div>
          <div className="relative mt-8 lg:mt-0 lg:absolute lg:right-16 lg:top-1/2 lg:-translate-y-1/2">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold px-7 py-4 hover:bg-[#155ed6] transition-colors shadow-lg"
            >
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export { ArrowRight };
